import { ReactNode } from "react";

import { createCellRenderer } from "./cellRenderers";

import type { ModuleUiColumnDefinition } from "@/features/integrations/types";
import type { TableColumn } from "@/types/table/tableProps";

/**
 * Process schema columns into TableColumn format.
 */
export function processColumnsToTableColumns(
  columns: ModuleUiColumnDefinition[] | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
  accessorMap?: Record<string, string>,
  customCellRenderers?: Record<
    string,
    (value: unknown, row: unknown) => ReactNode
  >,
): TableColumn<unknown>[] {
  if (!columns) {
    return [];
  }

  const effectiveAccessorMap = accessorMap ?? {};

  return columns.map((col) => {
    const accessor = effectiveAccessorMap[col.key] || col.key;
    const customRenderer = customCellRenderers?.[col.key];
    const schemaRenderer = createCellRenderer(col, t);

    return {
      header: t(col.label),
      accessor: accessor as string,
      ...(col.textAlign && {
        textAlign: col.textAlign as "left" | "right" | "center",
      }),
      ...(col.headerTooltip && { headerTooltip: t(col.headerTooltip) }),
      ...(typeof col.sortable === "boolean" && { sortable: col.sortable }),
      Cell: (row: unknown, _index: number): JSX.Element => {
        const value = (row as Record<string, unknown>)[accessor];
        const custom = customRenderer?.(value, row);
        const cellContent = custom ?? schemaRenderer(value, row, t);

        if (cellContent === null || cellContent === undefined) {
          return <span>{String(value ?? "")}</span>;
        }
        if (
          typeof cellContent === "string" ||
          typeof cellContent === "number" ||
          typeof cellContent === "boolean"
        ) {
          return <span>{String(cellContent)}</span>;
        }
        if (Array.isArray(cellContent)) {
          return <span>{cellContent}</span>;
        }
        return cellContent as JSX.Element;
      },
    };
  });
}
